/**
 * +prof: Mossvale's herb and ore nodes (docs/design/professions.md §3). ≈ 150 nodes in patches that
 * follow the zone's level bands: Sunleaf / Rustvein around Sunmeadow and town, Marshbell / Tallow
 * Seam along Glimmermere's shore and Barrowfield, Bloomcap / Ashen Vein at the forest edge and on
 * Greyhorn Heights, Starpetal / Coilstone Lode in the Thornheart, the Pale Rise ruins, on the
 * plateau's cliff ledges and deep in Tallowdeep Mine.
 *
 * +ore: ore only grows by rock: every mining node stands at the foot of a boulder, crag, cliff
 * band, ridge or spoil heap (ruin rubble from tier 3 up); the low-level country has rocky knolls
 * (mossvaleTerrain.ts OUTCROPS) for it. Herbs keep to meadows, shores and forest.
 *
 * Placement is generated once from the built zone (deterministic: a fixed seed per patch): a node
 * lands on walkable ground (inside the boundary, clear of every obstacle by a margin), outside safe
 * areas, away from NPCs, waystones, secrets and the barrow mouth, 7+ yd from other nodes, and, for
 * tiers 1–2, outside every hostile spawn point's aggro reach (its wander radius, patrols included).
 * Higher tiers may sit in dangerous spots on purpose. Because placement reads the zone's obstacles,
 * nodes follow terrain edits; the content test re-checks them against the real nav.
 *
 * This module attaches the nodes to `mossvale.field.gatherNodes` when it is imported (the zone index
 * imports it), so mossvaleZone.ts stays untouched.
 */
import type { DungeonDef, FieldDef, GatherNodeDef, Polygon, Vec2 } from '@keystone/sim';
import { FIELD_AGGRO_RADIUS } from '@keystone/sim';
import { mossvale } from './fields';
import { ROCK_FEATURES, type RockFeature, ZONE_SPOTS } from './mossvaleZone';
import { QUEST_NPCS } from './mossvaleQuestNpcs';
import { HILL, LAKE, PLATEAU_X0, plateauRimX, plateauRimY } from './mossvaleTerrain';
import { gatherKindAt, gatherTier, type GatherProfession } from './professions';

/** One patch: `count` nodes of a profession's tier scattered in a disc. */
interface Patch {
  prof: GatherProfession;
  tier: number;
  c: Vec2;
  r: number;
  count: number;
  /** Explicit spots (tried first, each nudged within `jitter` yd, default 3, to a valid one). */
  at?: readonly Vec2[];
  jitter?: number;
}

const S = ZONE_SPOTS;
const P = (x: number, y: number): Vec2 => ({ x, y });
const F: GatherProfession = 'foraging';
const M: GatherProfession = 'mining';

/** Points along the lake's shoreline ring (just outside the deep water). */
function shore(n: number, k: number, a0: number, a1: number): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = a0 + ((a1 - a0) * (i + 0.5)) / n;
    out.push(P(LAKE.c.x + Math.cos(a) * LAKE.rx * k, LAKE.c.y + Math.sin(a) * LAKE.ry * k));
  }
  return out;
}

/** Greyhorn's cliff ledges: on top of the south / west rims, a few yards back from the edge. */
const LEDGES: Vec2[] = [
  P(684, plateauRimY(684) + 3),
  P(760, plateauRimY(760) + 3),
  P(830, plateauRimY(830) + 3),
  P(plateauRimX(600) + 3, 600),
  P(plateauRimX(668) + 3, 668),
];

export const MOSSVALE_NODE_PATCHES: readonly Patch[] = [
  // ---- Tier 1 (Sunmeadow 1–3 and the country round town) -------------------------------------------
  { prof: F, tier: 1, c: S.slime, r: 82, count: 8 },
  { prof: F, tier: 1, c: S.town, r: 70, count: 6 },
  { prof: F, tier: 1, c: P(196, 228), r: 34, count: 4 },
  { prof: F, tier: 1, c: P(150, 330), r: 60, count: 3 },
  { prof: F, tier: 1, c: P(520, 190), r: 32, count: 3 },
  { prof: M, tier: 1, c: P(228, 78), r: 60, count: 7 },
  { prof: M, tier: 1, c: P(430, 236), r: 34, count: 6 },
  { prof: M, tier: 1, c: P(176, 296), r: 40, count: 4 },
  { prof: M, tier: 1, c: P(60, 380), r: 48, count: 4 },
  { prof: M, tier: 1, c: P(340, 52), r: 44, count: 6 },
  // ---- Tier 2 (Barrowfield 3–4, Glimmermere 4–5) ----------------------------------------------------
  { prof: F, tier: 2, c: LAKE.c, r: 0, count: 9, at: [...shore(12, 1.04, Math.PI * 0.5, Math.PI * 1.6), ...shore(8, 1.08, -Math.PI * 0.4, Math.PI * 0.3)], jitter: 9 },
  { prof: F, tier: 2, c: S.barrowfield, r: 66, count: 5 },
  { prof: F, tier: 2, c: P(296, 384), r: 30, count: 3 },
  { prof: F, tier: 2, c: P(118, 262), r: 22, count: 3 },
  { prof: M, tier: 2, c: P(452, 452), r: 64, count: 6 },
  { prof: M, tier: 2, c: P(626, 404), r: 32, count: 4 },
  { prof: M, tier: 2, c: P(706, 322), r: 36, count: 4 },
  { prof: M, tier: 2, c: P(560, 300), r: 40, count: 4 },
  // ---- Tier 3 (Greyhorn 5–7, Bramblewood's edge 7–8) ------------------------------------------------
  { prof: F, tier: 3, c: S.brambleEdge, r: 58, count: 7 },
  { prof: F, tier: 3, c: P(204, 572), r: 40, count: 4 },
  { prof: F, tier: 3, c: P(S.falls.x, S.falls.y - 30), r: 24, count: 3 },
  { prof: F, tier: 3, c: S.grotto, r: 20, count: 2 },
  { prof: F, tier: 3, c: P(322, 626), r: 28, count: 2 },
  { prof: M, tier: 3, c: S.heights, r: 70, count: 6 },
  { prof: M, tier: 3, c: P(664, 566), r: 28, count: 3 },
  { prof: M, tier: 3, c: P(756, 700), r: 30, count: 4 },
  { prof: M, tier: 3, c: P(782, 432), r: 40, count: 3 },
  // ---- The tier below, mixed into each band (WoW-style: a hero whose skill lags still finds nodes) ----
  { prof: F, tier: 1, c: S.barrowfield, r: 70, count: 2 },
  { prof: M, tier: 1, c: S.barrowfield, r: 70, count: 2 },
  { prof: F, tier: 1, c: P(672, 168), r: 50, count: 3 },
  { prof: M, tier: 1, c: P(640, 160), r: 50, count: 2 },
  { prof: M, tier: 1, c: P(628, 392), r: 34, count: 2 },
  { prof: M, tier: 1, c: P(272, 500), r: 40, count: 2 },
  { prof: M, tier: 2, c: P(700, 166), r: 50, count: 3 },
  { prof: M, tier: 2, c: P(650, 452), r: 36, count: 3 },
  { prof: F, tier: 2, c: P(700, 640), r: 70, count: 3 },
  { prof: M, tier: 2, c: P(736, 610), r: 70, count: 3 },
  { prof: F, tier: 2, c: S.brambleEdge, r: 60, count: 2 },
  { prof: M, tier: 2, c: P(236, 560), r: 60, count: 2 },
  { prof: F, tier: 3, c: P(150, 660), r: 60, count: 2 },
  { prof: M, tier: 3, c: P(140, 640), r: 60, count: 2 },
  { prof: F, tier: 3, c: HILL.c, r: 80, count: 2 },
  { prof: M, tier: 3, c: HILL.c, r: 80, count: 2 },
  // ---- Tier 4 (the Thornheart 8–9, the Pale Rise 9–10, ledges, the mine's depths) -------------------
  { prof: F, tier: 4, c: HILL.c, r: 48, count: 5 },
  { prof: F, tier: 4, c: P(110, 716), r: 60, count: 4 },
  { prof: F, tier: 4, c: S.lookout, r: 0, count: 1, at: [P(S.lookout.x - 6, S.lookout.y + 6)] },
  { prof: F, tier: 4, c: S.isle, r: 0, count: 1, at: [P(LAKE.isle.x - 2, LAKE.isle.y + 5)] },
  { prof: F, tier: 4, c: P(462, 756), r: 24, count: 2 },
  { prof: M, tier: 4, c: P(PLATEAU_X0, 600), r: 0, count: LEDGES.length, at: LEDGES },
  { prof: M, tier: 4, c: S.mine, r: 24, count: 4 },
  { prof: M, tier: 4, c: P(806, 758), r: 26, count: 2 },
  { prof: M, tier: 4, c: P(474, 704), r: 36, count: 2 },
];

// ---------------------------------------------------------------------------
// Geometry (exact polygon tests, like the nav's isWalkable)
// ---------------------------------------------------------------------------

function inPoly(p: Vec2, pg: Polygon): boolean {
  const pts = pg.points;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

interface Box {
  pg: Polygon;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}
const box = (pg: Polygon): Box => {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const q of pg.points) {
    x0 = Math.min(x0, q.x);
    y0 = Math.min(y0, q.y);
    x1 = Math.max(x1, q.x);
    y1 = Math.max(y1, q.y);
  }
  return { pg, x0, y0, x1, y1 };
};

const CELL = 16;
/** Obstacles bucketed in CELL yd cells (a big map has thousands). */
function bucket(obs: readonly Box[]): (p: Vec2) => readonly Box[] {
  const m = new Map<number, Box[]>();
  const key = (i: number, j: number): number => i * 4096 + j;
  for (const b of obs) {
    for (let i = Math.floor(b.x0 / CELL); i <= Math.floor(b.x1 / CELL); i++)
      for (let j = Math.floor(b.y0 / CELL); j <= Math.floor(b.y1 / CELL); j++) {
        const k = key(i, j);
        const l = m.get(k);
        if (l) l.push(b);
        else m.set(k, [b]);
      }
  }
  return (p) => m.get(key(Math.floor(p.x / CELL), Math.floor(p.y / CELL))) ?? [];
}

/** Mulberry32 (placement only; never the sim's RNG). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

function segDist(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}

/** Margin kept from obstacle edges (yd) and the minimum spacing between nodes. */
export const NODE_EDGE_MARGIN = 1.4;
export const NODE_MIN_SEP = 7;
/** Clearance from NPCs, waystones, secrets and portals. */
const PROP_CLEAR = 5;
/** Low-tier nodes keep this much beyond a hostile spawn's aggro reach. */
export const LOW_TIER_AGGRO_SLACK = 3;
/** Tiers up to this one stay out of hostile aggro reach. */
export const SAFE_TIERS = 2;

/** How close a hostile spawn's mobs can get to `p` minus their aggro radius (≥ 0 = outside reach). */
export function aggroClearance(field: Pick<FieldDef, 'spawns'>, p: Vec2): number {
  let worst = Infinity;
  for (const s of field.spawns) {
    const aggro = s.aggroRadius ?? FIELD_AGGRO_RADIUS;
    if (aggro <= 0) continue;
    const mv = s.movement;
    const wander = mv?.type === 'wander' ? mv.radius : 0;
    let d = dist(p, s.pos) - s.radius - wander;
    if (mv?.type === 'patrol') for (let i = 0; i < mv.points.length - 1; i++) d = Math.min(d, segDist(p, mv.points[i]!, mv.points[i + 1]!));
    worst = Math.min(worst, d - aggro);
  }
  return worst;
}

/** +ore: how far (yd beyond its radius) from its rock an ore node sits. */
export const ORE_ROCK_GAP: readonly [number, number] = [1.6, 4.6];

/** +ore: the rocky features inside a mining patch's disc (ruins only for tier 3+). */
function rocksNear(pt: Patch): RockFeature[] {
  return ROCK_FEATURES.filter((f) => dist(f.pos, pt.c) <= pt.r + 24 && (!f.ruin || pt.tier >= 3));
}

/** +ore: the point is at the foot of a rocky feature (the content test checks every ore node). */
export function byRock(p: Vec2, tier: number): boolean {
  return ROCK_FEATURES.some((f) => (!f.ruin || tier >= 3) && dist(f.pos, p) <= f.r + ORE_ROCK_GAP[1] + 4.5);
}

/** Build the nodes for a zone map (pure function of the map and the patches). */
export function placeGatherNodes(d: DungeonDef, patches: readonly Patch[] = MOSSVALE_NODE_PATCHES): GatherNodeDef[] {
  const field = d.field!;
  const rooms = d.rooms.map((r) => box(r.shape));
  const near = bucket((d.obstacles ?? []).map(box));
  const blocked = (p: Vec2): boolean => {
    if (!rooms.some((b) => p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1 && inPoly(p, b.pg))) return true;
    for (const b of near(p)) if (p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1 && inPoly(p, b.pg)) return true;
    return false;
  };
  const RING = Array.from({ length: 8 }, (_, k) => ({ x: Math.cos((k / 8) * Math.PI * 2), y: Math.sin((k / 8) * Math.PI * 2) }));
  const walkable = (p: Vec2): boolean => !blocked(p) && RING.every((o) => !blocked({ x: p.x + o.x * NODE_EDGE_MARGIN, y: p.y + o.y * NODE_EDGE_MARGIN }));
  const safe = (field.areas ?? []).filter((a) => a.safe);
  const props: Vec2[] = [...QUEST_NPCS.map((n) => n.pos), ...(field.npcs ?? []).map((n) => n.pos), // +ore: the side-quest NPCs too
    ...(field.waystones ?? []).map((w) => w.pos), ...(field.secrets ?? []).map((s) => s.pos), ...field.portals.map((q) => q.pos)];
  const out: GatherNodeDef[] = [];
  const counter: Record<string, number> = {};
  const ok = (p: Vec2, tier: number): boolean => {
    if (!walkable(p)) return false;
    if (safe.some((a) => inPoly(p, a.shape))) return false;
    if (props.some((q) => dist(q, p) < PROP_CLEAR)) return false;
    if (field.portals.some((q) => dist(q.pos, p) < q.radius + PROP_CLEAR + 2)) return false;
    if (out.some((n) => dist(n.pos, p) < NODE_MIN_SEP)) return false;
    if (tier <= SAFE_TIERS && aggroClearance(field, p) < LOW_TIER_AGGRO_SLACK) return false;
    return true;
  };
  patches.forEach((pt, pi) => {
    const kind = gatherKindAt(pt.prof, pt.tier);
    const t = gatherTier(pt.tier);
    const rand = rng(0x5eed_0000 + pi * 7919);
    let placed = 0;
    const add = (p: Vec2): void => {
      const n = (counter[kind.id] = (counter[kind.id] ?? 0) + 1);
      out.push({
        id: `${kind.id}_${String(n).padStart(2, '0')}`,
        kind: kind.id,
        profession: pt.prof,
        tier: pt.tier,
        req: t.req,
        pos: { x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 },
        facing: Math.round((rand() * Math.PI * 2 - Math.PI) * 1000) / 1000,
        channelMs: t.channelMs,
        respawnMs: t.respawnMs,
      });
      placed++;
    };
    // Explicit spots first (each nudged within 3 yd if it is not quite on walkable ground).
    for (const s of pt.at ?? []) {
      if (placed >= pt.count) break;
      for (let i = 0; i < 40; i++) {
        const a = rand() * Math.PI * 2;
        const r = i === 0 ? 0 : (pt.jitter ?? 3) * Math.sqrt(rand());
        const p = { x: s.x + Math.cos(a) * r, y: s.y + Math.sin(a) * r };
        if (ok(p, pt.tier)) {
          add(p);
          break;
        }
      }
    }
    // +ore: ore only by rock: at the foot of a boulder, crag, cliff or ridge (ruin rubble from tier 3).
    const rocks: readonly RockFeature[] = pt.prof === M ? rocksNear(pt) : [];
    for (let tries = 0; placed < pt.count && tries < 600 && pt.r > 0; tries++) {
      let p: Vec2;
      if (pt.prof === M) {
        if (!rocks.length) break;
        const f = rocks[Math.floor(rand() * rocks.length)]!;
        const a = rand() * Math.PI * 2;
        const r = f.r + ORE_ROCK_GAP[0] + rand() * (ORE_ROCK_GAP[1] - ORE_ROCK_GAP[0]);
        p = { x: f.pos.x + Math.cos(a) * r, y: f.pos.y + Math.sin(a) * r };
      } else {
        const a = rand() * Math.PI * 2;
        const r = pt.r * Math.sqrt(rand());
        p = { x: pt.c.x + Math.cos(a) * r, y: pt.c.y + Math.sin(a) * r };
      }
      if (ok(p, pt.tier)) add(p);
    }
  });
  return out;
}

/** The zone's nodes (also on `mossvale.field.gatherNodes`). */
export const MOSSVALE_NODES: readonly GatherNodeDef[] = placeGatherNodes(mossvale);
mossvale.field!.gatherNodes = MOSSVALE_NODES as GatherNodeDef[];
